import { Reference, useApolloClient } from '@apollo/client'
import {
  AttendanceWorkedTime,
  AttendanceWorkedTimeBlock,
  TimesheetWorkedTimeFragmentDoc,
  WorkedTimeAndBalanceRefreshDocument,
  WorkedTimeRefreshedDocument,
} from 'generated/resources/graphql'

import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'

import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import AttendanceFeatureUtils from 'modules/attendance/lib/utils/AttendanceFeatures'

interface Props {
  employeeId: number
  startOn: string
  endOn: string
}

type WorkedTime = Omit<
  AttendanceWorkedTime,
  'employee' | 'companyId' | 'employeeId' | 'timeUnit'
> & { __typename: 'AttendanceWorkedTime' }

type WorkedTimeBlock = Omit<AttendanceWorkedTimeBlock, 'employee' | 'companyId' | 'timeUnit'> & {
  __typename: 'AttendanceWorkedTimeBlock'
}

const useSubscriptionWorkedTimeRefreshed = ({ employeeId, startOn, endOn }: Props) => {
  const apolloClient = useApolloClient()
  const [getWorkedTimes] = useLazyQuery(WorkedTimeAndBalanceRefreshDocument)
  const { featureByName } = useTimesheetData()

  useSubscription(WorkedTimeRefreshedDocument, {
    onData: ({ data: refreshedData }) => {
      const refreshWorkedTime = refreshedData.data?.attendanceWorkedTimeRefresh?.workedTime

      if (!refreshWorkedTime || refreshWorkedTime?.employeeId != employeeId) return

      refreshWorkedTime.workedTimes.forEach((workedTime) => {
        apolloClient.cache.modify({
          id: `AttendanceEmployee:${employeeId}`,
          fields: {
            attendanceWorkedTimesConnection(existingConnection, { readField }) {
              const id = `${employeeId}_${workedTime.date}`

              const workedTimeBlocks: WorkedTimeBlock[] = (workedTime.workedTimeBlocks ?? []).map(
                (block) => ({
                  ...block,
                  __typename: 'AttendanceWorkedTimeBlock',
                  employeeId: employeeId,
                })
              )

              const workedTimeWithId: WorkedTime = {
                ...workedTime,
                id: id,
                workedTimeBlocks: workedTimeBlocks,
                __typename: 'AttendanceWorkedTime',
              }

              const newWorkedTimeRef = apolloClient.cache.writeFragment({
                data: workedTimeWithId,
                fragment: TimesheetWorkedTimeFragmentDoc,
                fragmentName: 'TimesheetWorkedTime',
              })

              if (
                (existingConnection.nodes || []).some(
                  (ref: Reference) => readField('id', ref) === id
                )
              ) {
                return existingConnection
              }

              return {
                ...existingConnection,
                nodes: [...existingConnection.nodes, newWorkedTimeRef],
                totalCount: existingConnection.totalCount + 1,
              }
            },
          },
        })
      })

      if (!AttendanceFeatureUtils.hasBalanceMaterialized(featureByName)) {
        getWorkedTimes({
          variables: {
            employeeId,
            startOn,
            endOn,
          },
          fetchPolicy: 'network-only',
          nextFetchPolicy: 'network-only',
        })
      }

      dispatch({
        type: EventTypes.ATTENDANCE_SHIFT_SAVED,
      })
    },
    skip: !AttendanceFeatureUtils.hasWorkedTimeMaterialized(featureByName),
  })
}

export default useSubscriptionWorkedTimeRefreshed
